import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { createAppQueryCache } from '@dealstack/frontend/shared/dataExchange/reactQuery/queryErrorHandler';
import { DebugValuesProvider } from '@dealstack/frontend/shared/components/DebugMenu/useDebugValues';
import { ModalLayer } from '@dealstack/frontend/shared/components/fundament/Modal/ModalLayer';
import {
	FullLoaderScreenLoaderProvider,
	FullscreenLoader,
} from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { AppTypeProvider } from '@dealstack/frontend/shared/mechanism/appTypeContext/appTypeContext';
import { BroadcastLayer } from '@dealstack/frontend/shared/mechanism/broadcast/broadcast';
import { ConfigCatWrapper } from '@dealstack/frontend/shared/mechanism/configCatFrontend/configCatFrontend';
import { CookieScriptComponent } from '@dealstack/frontend/shared/mechanism/cookie-script/cookieScript';
import { setupDebugging } from '@dealstack/frontend/shared/mechanism/debugging/setup';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { I18NLayer } from '@dealstack/frontend/shared/mechanism/i18n/i18nLayer';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { GlobalStyling } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { AppMantineProvider } from '@dealstack/frontend/shared/mechanism/styleguide/AppMantineProvider';
import { InitializeToastSystem } from '@dealstack/frontend/shared/mechanism/toast/InitializeToastSystem';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { InitializeTooltips } from '@dealstack/frontend/shared/mechanism/tooltip/InitializeTooltips';
import { RWDErrorModal } from '@dealstack/frontend/shared/pages/RWDErrorModal/RWDErrorModal';
import { useAppSelector } from '@dealstack/frontend/investor/hooks/redux';
import { Routing } from '@dealstack/frontend/investor/pages/Routing/Routing';
import { store } from '@dealstack/frontend/investor/redux/store';
import { PROJECT } from '@dealstack/shared/lib/common/environment';
import { apiWrapperSetup } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { ToastV2Host } from '@dealstack/frontend/shared/mechanism/toast/ToastV2Host';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import React, { Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { getWebEnvironment } from '@dealstack/frontend/shared/environment';
import { getGlobalConfig, useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { usePeriodicRefresh } from '@dealstack/frontend/shared/hooks/usePeriodicRefresh/usePeriodicRefresh.hook';
import { SessionTimeoutGuard } from '@dealstack/frontend/shared/components/SessionTimeout/SessionTimeoutGuard';
import { GenericError as GenericErrorPage } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { initializeSentry } from '@dealstack/frontend/shared/sentry';

// The investor QueryClientProvider mounts above the i18n layer, so the cache is
// constructed before `useTranslationDefault` is available. The generic
// query-error message is therefore resolved lazily through this holder, which
// ComponentWithTranslations populates once translations are ready (query errors
// can only fire after that point). This restores the v4 `queries.onError` toast
// that the react-query v5 bump removed.
let resolveGenericQueryError: ((error: unknown) => string | undefined) | undefined;

const queryClient = new QueryClient({
	// Cache-level error handling (v5 pattern). Per-query `meta` opt-ins
	// (meta.errorToast / meta.suppressErrorToast) take precedence; otherwise the
	// generic message below surfaces a toast, matching admin/app parity.
	queryCache: createAppQueryCache({
		showErrorToast: (message) => showToastV2({ title: message, state: 'error' }),
		resolveGenericMessage: (error) => resolveGenericQueryError?.(error),
	}),
});
// @d.slaby 06.09.2023
// This is set of debugging tools, should not be enabled on production
setupDebugging();

const environment = getWebEnvironment();
const [group] = environment;

initializeSentry({
	dsn: getGlobalConfig().sentryInvestor,
	apiUrl: getGlobalConfig().apiUrl,
	environment,
	release: import.meta.env.VITE_SENTRY_RELEASE,
});

logger.setConfig({
	enviroment: group,
});

export const InitializeApplication = () => {
	return (
		<React.StrictMode>
			<GlobalStyling />
			<Sentry.ErrorBoundary fallback={<GenericErrorPage />}>
				<FullLoaderScreenLoaderProvider>
					<QueryClientProvider client={queryClient}>
						<ThemeProvider theme={defaultTheme}>
							<AppMantineProvider>
								<ModalLayer>
									<InitializeTooltips />
									<Suspense fallback={<FullscreenLoader name="suspense" />}>
										<Provider store={store}>
											<ToastV2Host />
											<DebugValuesProvider>
												<RunInitialConfigurations />
											</DebugValuesProvider>
										</Provider>
									</Suspense>
								</ModalLayer>
							</AppMantineProvider>
						</ThemeProvider>
					</QueryClientProvider>
				</FullLoaderScreenLoaderProvider>
			</Sentry.ErrorBoundary>
		</React.StrictMode>
	);
};

const RunInitialConfigurations = () => {
	const globalConfig = useGlobalConfig();
	const globalState = useAppSelector((state) => state.global);
	const environment = getWebEnvironment();

	const userState = useAppSelector((state) => state.user);

	usePeriodicRefresh(userState);

	if (!globalConfig) {
		logger.info('[InitializeApplication] Missing GlobalConfig');

		return <GenericErrorPage />;
	}

	if (!globalConfig.configCatSdkKey) {
		throw new Error('ConfigCat SDK missing in GlobalConfig');
	}

	return (
		<ConfigCatWrapper sdkKey={globalConfig.configCatSdkKey}>
			<CookieScriptComponent project={PROJECT.ADMIN} environment={environment} />
			<AppTypeProvider appType={PROJECT.INVESTOR}>
				<I18NLayer>
					{globalState?.isLoadingTranslations ? (
						<FullscreenLoader name="translations" />
					) : (
						<>
							<RWDErrorModal />
							{/* v6 navigated synchronously; v7 defaults to startTransition, which keeps the old route mounted while the next one renders. */}
							<BrowserRouter useTransitions={false}>
								<ComponentWithTranslations />
								{userState.isLoading ? <FullscreenLoader name="userState" /> : null}
								<Routing />
								<InitializeToastSystem />
								<SessionTimeoutGuard />
								<BroadcastLayer />
							</BrowserRouter>
						</>
					)}
				</I18NLayer>
			</AppTypeProvider>
		</ConfigCatWrapper>
	);
};

// We need to create this component and implement it above after the I18NLayer
// as Translations must first be set before the useTranslationDefault hook is called
const ComponentWithTranslations = () => {
	const tDefault = useTranslationDefault();

	const queryClient = useQueryClient();

	useEffect(() => {
		const resolveMessage = (responseError: unknown) =>
			isStandardBackendError(responseError)
				? tDefault(`error_codes.${responseError.error?.code}`)
				: tDefault(`common_api_wrapper.local.message`);

		const onError = (responseError: unknown) => {
			// eslint-disable-next-line no-console
			console.error(responseError);

			showToastV2({
				title: resolveMessage(responseError),
				state: 'error',
			});
		};

		// Feed the cache-level resolver (queries) the same message logic used by
		// mutations (onError, below) and the legacy ApiWrapper local-error
		// handler, so all three error paths stay in parity.
		resolveGenericQueryError = resolveMessage;

		apiWrapperSetup({
			commonLocalErrorHandler: onError,
		});

		queryClient.setDefaultOptions({
			queries: {
				refetchOnWindowFocus: false,
				refetchOnReconnect: false,
				retry: false,
				retryOnMount: false,
				initialData: undefined,
				gcTime: 0,
			},
			mutations: {
				retry: false,
				onError,
			},
		});
	}, []);

	return null;
};
